export interface AiChat {
  id: string;
  title?: string;
  preview?: string;
  updatedAt?: number;
}
export type ChatEventSrc = 'user' | 'bot';

export interface ToolExecutionRecord {
  toolName: string;
  label: string;
  durationMs: number;
  failed: boolean;
}

export type ChatStatusType =
  | 'tool_started'
  | 'tool_completed'
  | 'tool_failed'
  | 'pending_approval';

interface ChatStatusBase {
  chatId: string;
}

export interface ChatStatusToolPayload extends ChatStatusBase {
  status: 'tool_started' | 'tool_completed' | 'tool_failed';
  toolName: string;
  label: string;
}

/**
 * SD-2326: fires once when the Langgraph proposeAction node interrupts. The BE
 * has registered a pending-approval row keyed by {@link pendingApprovalId} and
 * is waiting for the user's decision via POST `/api/v1/allo/approve-action`.
 * {@link cardJson} is a JSON-stringified {@code DynamicActionCardPayload}.
 */
export interface ChatStatusPendingApprovalPayload extends ChatStatusBase {
  status: 'pending_approval';
  pendingApprovalId: string;
  cardJson: string;
}

export type ChatStatusPayload =
  | ChatStatusToolPayload
  | ChatStatusPendingApprovalPayload;

export function isPendingApprovalStatus(
  status: ChatStatusPayload
): status is ChatStatusPendingApprovalPayload {
  return status.status === 'pending_approval';
}

/**
 * SD-2155 / SD-2153: wire-only payload pushed by the BE while Allo is
 * generating a response. The FE assembles the in-flight assistant bubble
 * from `delta` chunks; the canonical persisted {@link AiChatEvent} still
 * arrives at the end with the same `ts` so the bubble can reconcile.
 *
 * - `start`    — fires once before any tokens; FE creates an in-flight bubble
 *                keyed by `chatId+ts`.
 * - `delta`    — N batched chunks (~150ms apart). BE has already buffered any
 *                `<action-card>` blocks so `delta` text never splits a card.
 * - `complete` — fires once when the LLM finishes; the final `chatEvent` (op
 *                `ins`, ent `chatEvent`) follows shortly with the full text.
 * - `error`    — SD-2450: terminal failure marker sent *instead* of `complete`
 *                when the turn fails (e.g. a mid-stream connection reset). The
 *                FE flips the in-flight bubble to an interrupted state (keeping
 *                whatever partial text arrived) and offers a retry. No
 *                `chatEvent` follows. `code` distinguishes a retryable transport
 *                drop (`interrupted`) from any other failure (`failed`).
 */
export type ChatStreamType = 'start' | 'delta' | 'complete' | 'error';

/** SD-2450: failure class carried on a `type === 'error'` chatStream payload. */
export type ChatStreamErrorCode = 'interrupted' | 'failed';

export interface ChatStreamPayload {
  chatId: string;
  ts: number;
  type: ChatStreamType;
  /** Present only when `type === 'delta'`. */
  delta?: string;
  /** Monotonic per-stream counter starting at 0. Lets the FE detect drops. */
  seq?: number;
  /** Present only when `type === 'error'` (SD-2450). */
  code?: ChatStreamErrorCode;
}

export interface AiChatEvent {
  chatId: string;
  ts: number;
  src: ChatEventSrc;
  data: string;
  documentIds?: string[];
  toolsUsed?: ToolExecutionRecord[];
}
